import { useRef, useEffect } from 'react'

import { AbortController as _abortControllerPolyfill } from 'abortcontroller-polyfill/dist/cjs-ponyfill'

import useNonInitialEffect from '@aytm/app/stats/page/components/hooks/useNonInitialEffect'

import {
  DEFAULT_FETCH_TIMEOUT,
  FETCH_TIMEOUT_STEP,
  MAX_FETCH_TIMEOUT,
  FETCH_POLL_BUDGET_MS,
  FETCH_REQUEST_TIMEOUT_MS,
  FETCH_TOO_LONG_MESSAGE
} from '@aytm/app/stats/page/comments/lib/constants'

import PageGlobals from '@aytm/app/stats/lib/page/page-globals'
import { endPageLoading, getModelValue, loadQuestionsError } from '@aytm/app/stats/lib/stat-page'
import { convertTraits } from '@aytm/app/stats/lib/query/traits'
import { getRejectsAndKudos } from '@aytm/app/stats/page/comments/lib/utils'
import { fetchSearchResults } from '@aytm/app/stats/page/comments/apiCalls'

/* compat/compat disabled because polyfill is used */
const AbortController = window.AbortController || _abortControllerPolyfill

let fetchTimeout = DEFAULT_FETCH_TIMEOUT
let fetchDebounceTimeout = 0
let fetchTimer = null
let unknownStatusRetried = false

const useFetchDataImageResponses = ({
  apiKey,
  dispatch,
  dispatchCommentStatuses,
  searchStateRef,
  surveyData,
  setSurvey,
  specialMode,
  onFetchSchema,
  onFetch,
  onFetchApiKey
}) => {
  const abortController = useRef(null)

  const { isPreserveState, request } = searchStateRef.current
  const { query, selectedQuestion, pageSize, orderBy, orderDirection, currentPage } = request

  const fetchDataCallback = async data => {
    const { cs } = data

    dispatch({ type: 'SET_PARAM', request: { name: 'isProcessing', value: true } })

    const _survey = data.survey || surveyData.survey || {}

    const assets = data.assets || _survey.assets || {}
    const traits = convertTraits(_survey)

    let codeBooks = _survey.codeBooks
    if (!Array.isArray(codeBooks)) {
      codeBooks = Object.keys(codeBooks || {}).reduce(
        (acc, cur) => acc.concat(codeBooks[cur].map(codebook => Object.assign({ type: cur }, codebook))),
        []
      )
    }

    await setSurvey(
      Object.assign({}, _survey, {
        assets,
        traits,
        codeBooks
      })
    )

    dispatch({ type: 'SEARCH_RESULTS', data: { data, specialMode, pageSize } })
    dispatchCommentStatuses({
      type: 'BATCH_DATA',
      data: { batchData: getRejectsAndKudos(cs), isPreserveState }
    })
    dispatch({ type: 'SET_PARAM', request: { name: 'isPreserveState', value: false } })

    fetchTimeout = DEFAULT_FETCH_TIMEOUT
  }

  const tooLong = () => {
    fetchTimeout = DEFAULT_FETCH_TIMEOUT
    dispatch({ type: 'FAILURE', error: { name: 'CustomError', message: FETCH_TOO_LONG_MESSAGE } })
    endPageLoading()
  }

  const fetchData = async (
    {
      includeSchema = false,
      customParams = {},
      customFetchDataCallback = null,
      pollStartedAt = null,
      requestRetried = false
    } = {},
    fetchDataComplete = null
  ) => {
    let requestTimedOut = false
    let requestTimer = null

    try {
      if (null !== abortController.current) {
        abortController.current.abort()
        abortController.current = null
      }

      abortController.current = new AbortController()

      const { signal } = abortController.current
      const controller = abortController.current
      // a request that never answers is aborted here rather than left to hold the loader forever
      requestTimer = setTimeout(() => {
        requestTimedOut = true
        controller.abort()
      }, FETCH_REQUEST_TIMEOUT_MS)

      const data = await fetchSearchResults({
        query,
        selectedQuestion,
        currentPage,
        pageSize,
        orderBy,
        orderDirection,
        flattenAnswers: true,
        includeSchema: includeSchema || (!customFetchDataCallback && surveyData.survey == undefined),
        signal,
        ...customParams
      })
      clearTimeout(requestTimer)

      const callback = _data => {
        dispatch({ type: 'SET_PARAM', request: { name: 'isLoading', value: false } })

        fetchDataCallback(_data)
        if (fetchDataComplete && 'function' == typeof fetchDataComplete) {
          fetchDataComplete()
        }
      }

      switch (data.status) {
        case 'OK': {
          unknownStatusRetried = false

          if (null !== customFetchDataCallback) {
            dispatch({ type: 'SET_PARAM', request: { name: 'isLoading', value: false } })

            customFetchDataCallback(data)
          } else {
            if (
              PageGlobals.SHARE_KEY == undefined &&
              data.survey != undefined &&
              data.survey.target_market.quotas == undefined
            ) {
              if (onFetchSchema && 'function' == typeof onFetchSchema) {
                onFetchSchema(callback, data)
              }
            } else {
              callback(data)

              if (onFetch && 'function' == typeof onFetch) {
                onFetch(data)
              }
            }
          }
          break
        }
        case 'RUNNING': {
          // the same budget as the comments list: a job that never finishes ends on the error block
          const startedAt = pollStartedAt ?? Date.now()
          if (FETCH_POLL_BUDGET_MS <= Date.now() - startedAt) {
            tooLong()
            break
          }

          fetchTimeout = Math.min(MAX_FETCH_TIMEOUT, fetchTimeout + FETCH_TIMEOUT_STEP)
          fetchTimer = setTimeout(() => {
            fetchTimer = null
            fetchData(
              { includeSchema, customParams, customFetchDataCallback, pollStartedAt: startedAt },
              fetchDataComplete
            )
          }, fetchTimeout)
          break
        }
        case 'NO SCHEMA': {
          if (onFetchSchema && 'function' == typeof onFetchSchema) {
            onFetchSchema(callback, data)
          }
          break
        }
        case 'INVALID KEY': {
          if (onFetchApiKey && 'function' == typeof onFetchApiKey) {
            onFetchApiKey(fetchDataComplete)
          }
          break
        }
        default: {
          // the data service can drop a job it reported as running; a fresh key usually recovers it
          if (!unknownStatusRetried && onFetchApiKey && 'function' == typeof onFetchApiKey) {
            unknownStatusRetried = true
            onFetchApiKey(fetchDataComplete)
            break
          }

          unknownStatusRetried = false
          dispatch({ type: 'FAILURE', error: { name: 'CustomError', message: 'Something happen' } })
          // nothing else would take the page off its loading screen
          if (getModelValue('loading')) loadQuestionsError()
        }
      }
      fetchDebounceTimeout = 0

      abortController.current = null
    } catch (error) {
      clearTimeout(requestTimer)

      if (requestTimedOut) {
        abortController.current = null
        fetchDebounceTimeout = 0
        // one more ask before the page gives up: a single hung request is not a dead service
        if (!requestRetried) {
          fetchData(
            { includeSchema, customParams, customFetchDataCallback, pollStartedAt, requestRetried: true },
            fetchDataComplete
          )
        } else tooLong()
        return
      }

      if (!error.name || (error.name && 'AbortError' !== error.name)) {
        dispatch({ type: 'FAILURE', error })
      }

      fetchTimeout = DEFAULT_FETCH_TIMEOUT
      fetchDebounceTimeout = 0
    }
  }

  const fetchDataDelay = (force = false, fetchDataComplete) => {
    dispatch({ type: 'SEARCH_INIT' })

    fetchTimer && clearTimeout(fetchTimer)

    if (null !== abortController.current) {
      abortController.current.abort()
      abortController.current = null
    }

    const callback = () => {
      fetchTimer = null

      if (apiKey == undefined || null === apiKey) {
        // fetchDataCallback(getDefaultData())
        return
      }

      fetchData({ includeSchema: force }, fetchDataComplete)
    }

    if (0 < fetchDebounceTimeout) {
      fetchTimer = setTimeout(callback, fetchDebounceTimeout)
    } else callback()
  }

  // eslint-disable-next-line unicorn/consistent-function-scoping
  const fetchDataAbort = () => {
    fetchTimer && clearTimeout(fetchTimer)

    if (null !== abortController.current) {
      abortController.current?.abort()
      abortController.current = null
    }
  }

  // eslint-disable-next-line unicorn/consistent-function-scoping
  const fetchDataDestroy = () => {
    fetchTimeout = DEFAULT_FETCH_TIMEOUT
    fetchDebounceTimeout = 1000

    fetchDataAbort()
  }

  // normal mode fetch data effect
  useEffect(() => {
    fetchDataDelay(true)

    // a key that arrives after the mount is asked with at once: only a real unmount arms the grace period
    return () => fetchDataAbort()
    // disabled: fetchDataDelay is const
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => fetchDataDestroy(), [])

  // normal mode fetch data effect
  useNonInitialEffect(() => {
    fetchDataDelay()

    return () => fetchDataDestroy()
    // disabled: fetchDataDelay is const
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, selectedQuestion, pageSize, currentPage, orderBy, orderDirection])

  return [fetchData, fetchDataDelay]
}

export default useFetchDataImageResponses
